import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Ticket, DollarSign } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import GameDraw from '@/components/GameDraw';

const Play = () => {
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [betAmount, setBetAmount] = useState(5);
  const [gameType, setGameType] = useState('powerball');
  const [balance, setBalance] = useState(100); // Fake balance
  const [showDraw, setShowDraw] = useState(false);

  const numbers = Array.from({ length: 45 }, (_, i) => i + 1);

  const toggleNumber = (num: number) => {
    if (selectedNumbers.includes(num)) {
      setSelectedNumbers(selectedNumbers.filter(n => n !== num));
    } else if (selectedNumbers.length < 6) {
      setSelectedNumbers([...selectedNumbers, num]);
    }
  };

  const quickPick = () => {
    const randomNumbers = [];
    while (randomNumbers.length < 6) {
      const num = Math.floor(Math.random() * 45) + 1;
      if (!randomNumbers.includes(num)) {
        randomNumbers.push(num);
      }
    }
    setSelectedNumbers(randomNumbers.sort((a, b) => a - b));
  };

  const placeBet = () => {
    if (selectedNumbers.length !== 6) {
      alert('Please select exactly 6 numbers');
      return;
    }
    if (betAmount > balance) {
      alert('Insufficient balance');
      return;
    }
    
    setBalance(balance - betAmount);
    setShowDraw(true);
  };

  const handleDrawComplete = (winnings: number) => {
    if (winnings > 0) {
      setBalance(balance + winnings);
    }
    setTimeout(() => {
      setShowDraw(false);
      setSelectedNumbers([]);
    }, 5000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Play GoldenTicketLucky Lottery</h1>
          <p className="text-xl text-gray-700 mb-6">Choose your lucky numbers and win big!</p>
          <div className="bg-white bg-opacity-20 rounded-lg p-4 inline-block">
            <p className="text-2xl font-bold text-gray-900">
              Current Jackpot: $5,000,000
            </p>
          </div>
        </div>
      </section>

      {/* Game Draw Modal */}
      {showDraw && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <GameDraw
            selectedNumbers={selectedNumbers}
            betAmount={betAmount}
            onDrawComplete={handleDrawComplete}
          />
        </div>
      )}

      {/* Game Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Game Selection */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Ticket className="w-6 h-6 text-yellow-600" />
                    Select Your Numbers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">Game Type</label>
                    <Select value={gameType} onValueChange={setGameType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="powerball">PowerBall (6 numbers)</SelectItem>
                        <SelectItem value="oz-lotto">Oz Lotto (7 numbers)</SelectItem>
                        <SelectItem value="saturday-lotto">Saturday Lotto (6 numbers)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-gray-700">
                        Selected: {selectedNumbers.length}/6 numbers
                      </p>
                      <Button 
                        onClick={quickPick}
                        variant="outline"
                        className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                      >
                        Quick Pick
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-9 gap-2">
                      {numbers.map(num => (
                        <button
                          key={num}
                          onClick={() => toggleNumber(num)}
                          className={`
                            w-10 h-10 rounded-full text-sm font-medium transition-all
                            ${selectedNumbers.includes(num)
                              ? 'bg-yellow-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }
                          `}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">Bet Amount</label>
                    <Select value={betAmount.toString()} onValueChange={(value) => setBetAmount(Number(value))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">$5 - Single Game</SelectItem>
                        <SelectItem value="10">$10 - Double Chance</SelectItem>
                        <SelectItem value="25">$25 - Premium Play</SelectItem>
                        <SelectItem value="50">$50 - VIP Entry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={placeBet}
                    disabled={selectedNumbers.length !== 6}
                    className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 py-3"
                  >
                    Play Now - ${betAmount}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Balance */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900">Your Balance</h3>
                    <DollarSign className="w-5 h-5 text-yellow-600" />
                  </div>
                  <p className="text-3xl font-bold text-yellow-600">${balance}</p>
                  <Button className="w-full mt-4 bg-yellow-600 hover:bg-yellow-700">
                    Add Funds
                  </Button>
                </CardContent>
              </Card>

              {/* Next Draw */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Next Draw</h3>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-600">2 Days</p>
                    <p className="text-gray-600">15 Hours 23 Minutes</p>
                    <p className="text-sm text-gray-500 mt-2">Saturday 8:00 PM AEDT</p>
                  </div>
                </CardContent>
              </Card>

              {/* Odds */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Prize Structure</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Division 1 (6 numbers)</span>
                      <span className="font-medium">$5,000,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Division 2 (5 numbers + sup)</span>
                      <span className="font-medium">$25,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Division 3 (5 numbers)</span>
                      <span className="font-medium">$2,500</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Division 4 (4 numbers)</span>
                      <span className="font-medium">$150</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Play;
